/**
 * Jinja2 Template Utilities for Frontend
 *
 * Provides Jinja2-compatible template rendering and variable extraction
 * for the frontend template execution system.
 */

/**
 * Check if a variable name is a valid variable (not escaped syntax or invalid)
 *
 * @param {string} variableName - The variable name to check
 * @returns {boolean} True if it's a valid variable name
 */
const isValidVariableName = variableName => {
  if (!variableName || typeof variableName !== 'string') {
    return false;
  }

  const trimmed = variableName.trim();

  // Empty strings are invalid
  if (trimmed.length === 0) {
    return false;
  }

  // Filter out escaped syntax patterns (with or without quotes)
  // These are patterns used to escape Jinja2 syntax in templates
  const escapedPatterns = [
    "'{{'",
    "'}}'",
    "'{{",
    "}}'",
    "{{'",
    "}}'",
    '{{',
    '}}',
    "'",
    '"',
  ];

  // Check exact matches
  if (escapedPatterns.includes(trimmed)) {
    return false;
  }

  // Filter out variables that start or end with quotes (likely escaped syntax)
  if (trimmed.startsWith("'") || trimmed.endsWith("'")) {
    return false;
  }
  if (trimmed.startsWith('"') || trimmed.endsWith('"')) {
    return false;
  }

  // Filter out variables that contain curly braces (likely escaped syntax)
  if (trimmed.includes('{{') || trimmed.includes('}}')) {
    return false;
  }

  // Filter out single character special characters
  if (trimmed.length === 1 && !/^[a-zA-Z_0-9]$/.test(trimmed)) {
    return false;
  }

  // Valid variable names should:
  // - Start with a letter or underscore
  // - Contain only letters, numbers, underscores, and dots (for nested properties)
  // - Not be empty
  const validVariablePattern = /^[a-zA-Z_][a-zA-Z0-9_.]*$/;
  if (!validVariablePattern.test(trimmed)) {
    return false;
  }

  return true;
};

/**
 * Remove content within {% raw %} blocks to avoid parsing variables from examples
 *
 * @param {string} templateContent - The template content
 * @returns {string} Template content with raw blocks removed
 */
const removeRawBlocks = templateContent => {
  if (!templateContent || typeof templateContent !== 'string') {
    return templateContent;
  }

  try {
    // Remove content between {% raw %} and {% endraw %} tags
    // This handles both single-line and multi-line raw blocks
    const rawBlockRegex = /\{%\s*raw\s*%\}([\s\S]*?)\{%\s*endraw\s*%\}/gi;
    return templateContent.replace(rawBlockRegex, '');
  } catch (error) {
    // If removal fails, return original content
    return templateContent;
  }
};

/**
 * Parse Jinja2 template variables using a more sophisticated approach
 * that handles Jinja2 syntax including conditionals, loops, and filters.
 * Now includes filtering to exclude false positives like escaped syntax.
 *
 * @param {string} templateContent - The template content to parse
 * @returns {Array<string>} Array of unique variable names
 */
export const parseJinja2Variables = templateContent => {
  if (!templateContent || typeof templateContent !== 'string') {
    return [];
  }

  try {
    // First, remove content within {% raw %} blocks to avoid parsing examples
    const contentWithoutRawBlocks = removeRawBlocks(templateContent);

    // Enhanced regex to capture Jinja2 variables in various contexts
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const variables = [];
    let match;

    while ((match = variableRegex.exec(contentWithoutRawBlocks)) !== null) {
      const variableName = match[1]?.trim();

      if (variableName && typeof variableName === 'string') {
        // Handle filters (e.g., "name|upper" -> "name")
        const cleanVariable = variableName.split('|')[0].trim();

        // Handle nested variables (e.g., "user.name" -> "user")
        const rootVariable = cleanVariable.split('.')[0].trim();

        // Validate that this is a real variable name, not escaped syntax
        if (
          rootVariable &&
          isValidVariableName(rootVariable) &&
          !variables.includes(rootVariable)
        ) {
          variables.push(rootVariable);
        }
      }
    }

    return variables;
  } catch (error) {
    // Error parsing Jinja2 variables
    return [];
  }
};

/**
 * Generate Jinja2 template preview with enhanced rendering
 * that handles Jinja2 syntax including conditionals and loops.
 *
 * @param {string} templateContent - The template content
 * @param {Object} inputData - The input data for rendering
 * @returns {string} Rendered template preview
 */
export const generateJinja2Preview = (templateContent, inputData) => {
  if (!templateContent) return 'Template not loaded';

  try {
    let preview = String(templateContent);

    if (
      inputData &&
      typeof inputData === 'object' &&
      Object.keys(inputData).length > 0
    ) {
      // Handle simple variable substitution first
      Object.entries(inputData).forEach(([key, value]) => {
        if (key && typeof key === 'string' && value !== undefined) {
          try {
            // Replace {{variable}} patterns
            const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
            const safeValue = typeof value === 'object' ? String(value) : value;
            preview = preview.replace(regex, String(safeValue) || `{{${key}}}`);

            // Also handle filtered variables like {{variable|filter}}
            const filteredRegex = new RegExp(`\\{\\{${key}\\|[^}]+\\}\\}`, 'g');
            preview = preview.replace(
              filteredRegex,
              String(safeValue) || `{{${key}}}`
            );
          } catch (regexError) {
            // Regex error for key
          }
        }
      });

      // Handle basic Jinja2 conditionals (simplified frontend rendering)
      preview = renderJinja2Conditionals(preview, inputData);

      // Handle basic Jinja2 loops (simplified frontend rendering)
      preview = renderJinja2Loops(preview, inputData);
    }

    return preview || 'No preview available';
  } catch (error) {
    // Error generating Jinja2 preview
    return 'Error generating preview';
  }
};

/**
 * Render basic Jinja2 conditionals in the frontend
 * This is a simplified implementation for preview purposes
 *
 * @param {string} template - The template content
 * @param {Object} data - The input data
 * @returns {string} Template with conditionals rendered
 */
const renderJinja2Conditionals = (template, data) => {
  try {
    // Simple conditional rendering for preview
    // This handles basic {% if condition %}...{% endif %} patterns
    const conditionalRegex =
      /\{%\s*if\s+([^%]+)\s*%\}([\s\S]*?)\{%\s*endif\s*%\}/g;

    return template.replace(conditionalRegex, (match, condition, content) => {
      try {
        // Simple condition evaluation for preview
        const conditionResult = evaluateSimpleCondition(condition, data);
        return conditionResult ? content : '';
      } catch (error) {
        // Show content if condition evaluation fails
        return content;
      }
    });
  } catch (error) {
    // Error rendering conditionals
    return template;
  }
};

/**
 * Render basic Jinja2 loops in the frontend
 * This is a simplified implementation for preview purposes
 *
 * @param {string} template - The template content
 * @param {Object} data - The input data
 * @returns {string} Template with loops rendered
 */
const renderJinja2Loops = (template, data) => {
  try {
    // Simple loop rendering for preview
    // This handles basic {% for item in items %}...{% endfor %} patterns
    const loopRegex =
      /\{%\s*for\s+(\w+)\s+in\s+(\w+)\s*%\}([\s\S]*?)\{%\s*endfor\s*%\}/g;

    return template.replace(loopRegex, (match, itemVar, arrayVar, content) => {
      try {
        const array = data[arrayVar];
        if (Array.isArray(array)) {
          return array
            .map((item, index) => {
              // Replace {{item.property}} with actual values
              let loopContent = content;
              Object.entries(item).forEach(([key, value]) => {
                const itemRegex = new RegExp(
                  `\\{\\{${itemVar}\\.${key}\\}\\}`,
                  'g'
                );
                loopContent = loopContent.replace(itemRegex, String(value));
              });
              return loopContent;
            })
            .join('');
        }
        return content; // Show content if array is not found
      } catch (error) {
        // Show content if loop rendering fails
        return content;
      }
    });
  } catch (error) {
    // Error rendering loops
    return template;
  }
};

/**
 * Evaluate simple conditions for Jinja2 preview
 * This is a basic implementation for frontend preview
 *
 * @param {string} condition - The condition to evaluate
 * @param {Object} data - The input data
 * @returns {boolean} Result of the condition
 */
const evaluateSimpleCondition = (condition, data) => {
  try {
    // Handle simple comparisons like "score >= 90"
    const comparisonRegex = /(\w+)\s*([><=!]+)\s*(\w+)/;
    const match = condition.match(comparisonRegex);

    if (match) {
      const [, variable, operator, value] = match;
      const variableValue = data[variable];
      const compareValue = isNaN(value) ? value : Number(value);

      switch (operator) {
        case '>=':
          return Number(variableValue) >= Number(compareValue);
        case '<=':
          return Number(variableValue) <= Number(compareValue);
        case '>':
          return Number(variableValue) > Number(compareValue);
        case '<':
          return Number(variableValue) < Number(compareValue);
        case '==':
        case '===':
          return variableValue === compareValue;
        case '!=':
        case '!==':
          return variableValue !== compareValue;
        default:
          return Boolean(variableValue);
      }
    }

    // Handle simple variable existence
    const variable = condition.trim();
    return Boolean(data[variable]);
  } catch (error) {
    // Error evaluating condition
    return false;
  }
};

/**
 * Validate Jinja2 template variables
 * Enhanced validation that handles Jinja2-specific features
 *
 * @param {Object} template - The template object
 * @param {Object} executionData - The execution data
 * @returns {Object} Validation result with missing variables
 */
export const validateJinja2Variables = (template, executionData) => {
  if (!template?.template) {
    return { isValid: true, missingVariables: [] };
  }

  try {
    const variables = parseJinja2Variables(template.template);
    const missingVariables = variables.filter(
      variable =>
        variable &&
        typeof variable === 'string' &&
        (!executionData.input_data[variable] ||
          executionData.input_data[variable].trim() === '')
    );

    return {
      isValid: missingVariables.length === 0,
      missingVariables,
      allVariables: variables,
    };
  } catch (error) {
    // Error validating Jinja2 variables
    return { isValid: false, missingVariables: [] };
  }
};

/**
 * Enhanced template preview with Jinja2 support
 * This function provides a more sophisticated preview that handles
 * Jinja2 syntax including conditionals, loops, and filters.
 *
 * @param {Object} template - The template object
 * @param {Object} inputData - The input data
 * @returns {string} Enhanced template preview
 */
export const generateEnhancedJinja2Preview = (template, inputData) => {
  if (!template?.template) return 'Template not loaded';

  try {
    return generateJinja2Preview(template.template, inputData);
  } catch (error) {
    // Error generating enhanced Jinja2 preview
    return 'Error generating preview';
  }
};

/**
 * Get Jinja2 template statistics
 * Provides information about the template's Jinja2 features
 *
 * @param {string} templateContent - The template content
 * @returns {Object} Template statistics
 */
export const getJinja2TemplateStats = templateContent => {
  if (!templateContent) {
    return {
      hasConditionals: false,
      hasLoops: false,
      hasFilters: false,
      variableCount: 0,
      complexity: 'simple',
    };
  }

  try {
    const hasConditionals = /\{%\s*if\s+[^%]+\s*%\}/.test(templateContent);
    const hasLoops = /\{%\s*for\s+\w+\s+in\s+\w+\s*%\}/.test(templateContent);
    const hasFilters = /\{\{[^}]+\|[^}]+\}\}/.test(templateContent);
    const variableCount = parseJinja2Variables(templateContent).length;

    let complexity = 'simple';
    if (hasConditionals && hasLoops) {
      complexity = 'complex';
    } else if (hasConditionals || hasLoops) {
      complexity = 'moderate';
    }

    return {
      hasConditionals,
      hasLoops,
      hasFilters,
      variableCount,
      complexity,
    };
  } catch (error) {
    // Error analyzing Jinja2 template
    return {
      hasConditionals: false,
      hasLoops: false,
      hasFilters: false,
      variableCount: 0,
      complexity: 'simple',
    };
  }
};
